/**
 * 이상한 방 - 로비의 두 조각: 참가자 목록 · 방장 설정 (순수 DOM)
 *
 * 근거 문서: `이상한방-상태모델-v0.1.md` 5.3절(방 규칙 - 허용 동사 · 개수) · 로드맵 M6(로비 - 참가자(사람·이웃 표시) · 방장 설정(동사·인당 이상한 곳 수))
 *
 * `lobby.ts`에서 갈라져 나왔다(400줄 목표). 둘 다 **받은 것을 그린다** - 누가 방장인지·준비인지·이웃인지는 호스트의 뷰가 말한 값이고
 * (`app/lobbyFlow.toLobbyRoomView`), 설정의 지금 값도 뷰(`ClientView.config`)에서 온다. 여기서 "전원 준비인가"를 세거나 동사 조합의
 * 유효성을 판단하지 않는다 - 그것은 리듀서(`matchState.canStart` · `configure`)의 것이다.
 *
 * ## 예외 하나 - 내 별명
 *
 * 별명 한 줄만은 뷰에 없는 값을 읽는다(`ui/cosmeticVars.myNicknameLabel()`). 남의 사물함은 뷰에 오지 않으므로
 * (호스트가 뷰를 만들고 호스트는 계정 저장을 모른다 - `matchView.ts`) 별명은 **내 줄에만** 붙고, 그 값의 출처는
 * 모듈 수준 상태다. 선례는 `ui/settings.isSettingsOpen()`이다. 같은 입력으로 두 번 그려도 결과가 같지만,
 * `applyCosmetics`를 부르기 전과 후는 다르다는 뜻이다.
 *
 * ## 표식은 글자다
 *
 * 방장 · 이웃 · 준비됨 · 기다리는 중 · 끊김이 전부 작은 태그 글자다(`playerStrip.ts`와 같은 결정). 이 게임의 색은 노랑 하나라 상태마다
 * 색을 배정할 수 없고, 색각 이상에서도 읽혀야 한다. 노랑이 붙는 자리는 "준비됨" 하나 - 방 안에서 사람이 기다리는 사건이 그것이다.
 *
 * ## 설정은 방장이 아니어도 보인다 - 잠긴 채로
 *
 * 남들도 "이번 방은 옮김만, 8개"를 알아야 한다(공정 - 모두에게 같은 정보). 방장이 아니면 버튼이 잠긴다. 지우지 않는 이유는 잠긴 버튼이
 * 제자리에 남아야 손가락이 헛디디지 않고(`changerBar.ts` 머리말), 무엇을 방장이 바꿀 수 있는지가 남에게도 보이기 때문이다.
 * 마지막 하나 남은 종류는 끌 수 없다 - 종류 0의 방은 이상한 곳을 만들 수 없고, 그것은 규칙이 아니라 "빈 방을 만들지 않는 버튼"이다.
 */

import type { Verb } from '../core/types'
import { VERBS } from '../core/types'
import { CHANGE_COUNT_MIN } from '../game/match/matchState'
import { t } from '../i18n'

import { myNicknameLabel } from './cosmeticVars'
import { createElement, setAttr, setDisabled, setFlag, setHidden, setText } from './dom'

/* --- 타입 ---------------------------------------------------------------------- */

/** 좌석 한 줄. 값은 전부 호스트의 뷰에서 옮겨 온 것이다. */
export type LobbySeatRow = Readonly<{
  id: string
  name: string
  isMe: boolean
  isHost: boolean
  /** CPU 좌석. 화면에서는 '이웃'이다(GLOSSARY 1.5). */
  isNeighbor: boolean
  ready: boolean
  connected: boolean
}>

export type LobbySettingsView = Readonly<{
  allowedVerbs: readonly Verb[]
  /** **인당** 이상한 곳 수(M11). 라운드 전체는 좌석 수 × 이 값이다 - 총량을 손잡이로 두면 사람마다의 몫이 인원에 따라 흔들린다. */
  changeCount: number
  /** 1라운드를 꾸미기로 시작하는가(M10). 빈 방에 전원이 소품을 놓고 그 방으로 판을 돈다. */
  humanDecorate: boolean
}>

/** 방장이 보내는 설정 변경. 한 번에 하나만 바꾼다 - `configure.patch`로 그대로 조립된다(`app/lobbyFlow.ts`). */
export type LobbySettingsPatch =
  | Readonly<{ kind: 'verbs'; allowedVerbs: readonly Verb[] }>
  | Readonly<{ kind: 'count'; changeCount: number }>
  | Readonly<{ kind: 'decorate'; humanDecorate: boolean }>

/* --- 상수 ---------------------------------------------------------------------- */

/**
 * 스테퍼의 범위. 하한은 **규칙의 것을 그대로 가져온다**(`matchState.CHANGE_COUNT_MIN` - 0개짜리 라운드가 없다는 것은 화면의
 * 사정이 아니다). 상한만 이 파일의 것이다 - 규칙의 상한은 200(`matchState.CHANGE_COUNT_MAX`, `?changes=`와 같은 값)이지만
 * 그것은 URL 도구의 것이고, 로비의 스테퍼는 손가락 한 번에 하나라 24를 넘는 값은 이 버튼으로 갈 자리가 아니다.
 *
 * 잠금은 게이트가 아니라 게이트의 얼굴이다 - 범위 밖의 `configure`는 리듀서(`matchState.normalizeRoomConfig`)가 접는다.
 */
export { CHANGE_COUNT_MIN }
export const CHANGE_COUNT_MAX = 24

/* --- 참가자 목록 ------------------------------------------------------------------ */

export type SeatListHandle = Readonly<{
  root: HTMLElement
  render: (seats: readonly LobbySeatRow[], max: number) => void
}>

/** `nick`은 별명 줄이다. **내 좌석에만** 글자가 온다(로드맵 M9 - `ui/cosmeticVars.ts` 머리말). */
type SeatNodes = Readonly<{ root: HTMLLIElement; name: HTMLElement; nick: HTMLElement; tags: HTMLElement }>

export function createSeatList(): SeatListHandle {
  const root = createElement('section', 'lb-seats')
  const count = createElement('p', 'lb-seats__count', '')
  /*
   * 좌석 수 옆 한 줄(M11) - "구획은 인원 수만큼 생긴다". 방을 만들기 **전에** 보여야 하는 사실이다: 사람이 하나 늘면
   * 평면도가 통째로 바뀌고(`core/partyPlan.ts`) 각자 맡는 곳이 하나씩 는다. 인원이 곧 방의 모양이라는 것을
   * 화면이 말하지 않으면 "왜 방이 아까랑 다르지"가 남는다.
   */
  const zoneNote = createElement('p', 'lb-seats__zones', '')
  const list = createElement('ul', 'lb-seats__list')
  root.append(count, zoneNote, list)
  const nodes = new Map<string, SeatNodes>()

  function tag(key: string, className: string): HTMLElement {
    return createElement('span', `lb-tag ${className}`, t(key))
  }

  function paintTags(node: SeatNodes, seat: LobbySeatRow): void {
    const tags: HTMLElement[] = []
    if (seat.isHost) tags.push(tag('lobby.tag.host', 'lb-tag--host'))
    if (seat.isNeighbor) tags.push(tag('lobby.tag.neighbor', 'lb-tag--neighbor'))
    if (!seat.connected) tags.push(tag('lobby.tag.away', 'lb-tag--away'))
    else if (seat.ready) tags.push(tag('lobby.tag.ready', 'lb-tag--ready'))
    else tags.push(tag('lobby.tag.waiting', 'lb-tag--waiting'))
    node.tags.replaceChildren(...tags)
  }

  return {
    root,
    render(seats, max): void {
      setText(count, t('lobby.seats', { count: seats.length, max }))
      setText(zoneNote, t('zone.lobby.note'))
      setAttr(root, 'aria-label', t('lobby.seatsAria', { count: seats.length, max }))
      // 좌석 목록이 바뀔 때만 줄을 다시 만든다(`playerStrip.ts`와 같은 규약). 값은 매번 다시 칠한다 - 태그는 몇 개라 그대로 갈아 끼운다.
      const ids = seats.map((seat) => seat.id)
      if (ids.length !== nodes.size || ids.some((id) => !nodes.has(id))) {
        nodes.clear()
        list.replaceChildren()
        for (const seat of seats) {
          const li = createElement('li', 'lb-seat')
          const name = createElement('span', 'lb-seat__name', '')
          const nick = createElement('span', 'lb-seat__nick', '')
          const tags = createElement('span', 'lb-seat__tags')
          li.append(name, nick, tags)
          list.append(li)
          nodes.set(seat.id, { root: li, name, nick, tags })
        }
      }
      for (const seat of seats) {
        const node = nodes.get(seat.id)
        if (node === undefined) continue
        setText(node.name, seat.name)
        // 별명은 내 좌석에만. 없으면 노드째 접는다 - 빈 줄이 서면 좌석 높이가 사람마다 달라진다.
        const nickname = seat.isMe ? myNicknameLabel() : null
        setHidden(node.nick, nickname === null)
        if (nickname !== null) setText(node.nick, nickname)
        setFlag(node.root, 'is-me', seat.isMe)
        setFlag(node.root, 'is-away', !seat.connected)
        paintTags(node, seat)
      }
    },
  }
}

/* --- 방장 설정 -------------------------------------------------------------------- */

export type HostSettingsHandle = Readonly<{
  root: HTMLElement
  /** `editable`이 거짓이면 전부 잠긴다(방장이 아니다). */
  render: (settings: LobbySettingsView, editable: boolean) => void
  /** 언어가 바뀌었다 - 굳은 라벨을 다시 칠한다. */
  repaint: () => void
}>

export function createHostSettings(onChange: (patch: LobbySettingsPatch) => void): HostSettingsHandle {
  const root = createElement('section', 'lb-settings')
  const title = createElement('h2', 'lb-settings__title', '')

  // 종류
  const verbsLabel = createElement('p', 'lb-settings__label', '')
  const verbsRow = createElement('div', 'lb-settings__row')
  const verbButtons = new Map<Verb, HTMLButtonElement>()
  for (const verb of VERBS) {
    const button = createElement('button', 'lb-chip', '')
    button.type = 'button'
    button.setAttribute('aria-pressed', 'false')
    verbButtons.set(verb, button)
    verbsRow.append(button)
  }

  // 수
  const countLabel = createElement('p', 'lb-settings__label', '')
  const countRow = createElement('div', 'lb-settings__row lb-settings__row--count')
  const less = createElement('button', 'lb-step', '-')
  less.type = 'button'
  const countValue = createElement('span', 'lb-settings__value', '')
  const more = createElement('button', 'lb-step', '+')
  more.type = 'button'
  countRow.append(less, countValue, more)

  /*
   * 꾸미기(M10). 켜고 끄는 것 하나라 스테퍼가 아니라 누르는 칩 하나다 - 동사 칩과 같은 문법(`aria-pressed`)이라
   * 로비에 새 위젯 종류가 생기지 않는다. 설명 한 줄이 붙는 이유는 이 토글만 **라운드 구조**를 바꾸기 때문이다.
   */
  const decorateLabel = createElement('p', 'lb-settings__label', '')
  const decorateRow = createElement('div', 'lb-settings__row')
  const decorateChip = createElement('button', 'lb-chip', '')
  decorateChip.type = 'button'
  decorateChip.setAttribute('aria-pressed', 'false')
  decorateRow.append(decorateChip)

  root.append(title, verbsLabel, verbsRow, countLabel, countRow, decorateLabel, decorateRow)

  let current: LobbySettingsView | null = null
  let editable = false

  function verbsAfterToggle(verb: Verb): readonly Verb[] {
    const on = new Set(current?.allowedVerbs ?? [])
    if (on.has(verb)) on.delete(verb)
    else on.add(verb)
    // 정본 순서로 돌려준다 - 뷰가 그 순서로 오므로 왕복해도 같은 배열이다.
    return VERBS.filter((candidate) => on.has(candidate))
  }

  for (const [verb, button] of verbButtons) {
    button.addEventListener('click', () => {
      if (!editable || current === null) return
      onChange({ kind: 'verbs', allowedVerbs: verbsAfterToggle(verb) })
    })
  }
  less.addEventListener('click', () => {
    if (!editable || current === null || current.changeCount <= CHANGE_COUNT_MIN) return
    onChange({ kind: 'count', changeCount: current.changeCount - 1 })
  })
  more.addEventListener('click', () => {
    if (!editable || current === null || current.changeCount >= CHANGE_COUNT_MAX) return
    onChange({ kind: 'count', changeCount: current.changeCount + 1 })
  })
  decorateChip.addEventListener('click', () => {
    if (!editable || current === null) return
    onChange({ kind: 'decorate', humanDecorate: !current.humanDecorate })
  })

  function repaint(): void {
    setText(title, t('lobby.settings'))
    setText(verbsLabel, t('lobby.settings.verbs'))
    setText(countLabel, t('lobby.settings.count'))
    setText(decorateLabel, t('lobby.settings.decorate'))
    setText(decorateChip, t('lobby.settings.decorate.on'))
    setAttr(less, 'aria-label', t('lobby.settings.less'))
    setAttr(more, 'aria-label', t('lobby.settings.more'))
    for (const [verb, button] of verbButtons) setText(button, t(`verb.${verb}`))
  }

  repaint()

  return {
    root,
    repaint,
    render(settings, nextEditable): void {
      current = settings
      editable = nextEditable
      setFlag(root, 'is-locked', !editable)
      const on = new Set(settings.allowedVerbs)
      for (const [verb, button] of verbButtons) {
        const pressed = on.has(verb)
        setAttr(button, 'aria-pressed', pressed ? 'true' : 'false')
        // 마지막 하나는 끌 수 없다(머리말).
        setDisabled(button, !editable || (pressed && on.size <= 1))
      }
      setText(countValue, String(settings.changeCount))
      setDisabled(less, !editable || settings.changeCount <= CHANGE_COUNT_MIN)
      setDisabled(more, !editable || settings.changeCount >= CHANGE_COUNT_MAX)
      setAttr(decorateChip, 'aria-pressed', settings.humanDecorate ? 'true' : 'false')
      setDisabled(decorateChip, !editable)
    },
  }
}
